"use client"

import { useState, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import type { ApiResponse, FieldMapping } from "../types/field-mapping"
import { FIELD_MAPPING_CONSTANTS } from "../constants/field-mapping"

export const useFieldData = () => {
  const { getAllFields } = useAuth()
  const location = useLocation()

  const [hubspotFields, setHubspotFields] = useState<string[]>([])
  const [salesforceFields, setSalesforceFields] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFieldData = useCallback(async (): Promise<FieldMapping[]> => {
    try {
      setLoading(true)
      setError(null)

      // Get parameters
      const tenantId = localStorage.getItem(FIELD_MAPPING_CONSTANTS.STORAGE_KEYS.HUBSPOT_TENANT_ID)
      const queryParams = new URLSearchParams(location.search)
      const connectorId = queryParams.get("id")

      // Validate required parameters
      if (!tenantId) {
        throw new Error(FIELD_MAPPING_CONSTANTS.ERROR_MESSAGES.NO_TENANT_ID)
      }

      if (!connectorId) {
        throw new Error(FIELD_MAPPING_CONSTANTS.ERROR_MESSAGES.NO_CONNECTOR_ID)
      }

      // Fetch field data
      const response: ApiResponse = await getAllFields(Number(tenantId), Number(connectorId))

      if (response.status !== 200) {
        throw new Error(response.message || "Failed to fetch field data")
      }

      const { sourceFields, targetFields, suggestedMappings } = response.data

      // Validate response data
      if (!sourceFields || !targetFields) {
        throw new Error(FIELD_MAPPING_CONSTANTS.ERROR_MESSAGES.INVALID_FIELD_DATA)
      }

      // Set field data
      setHubspotFields(sourceFields)
      setSalesforceFields(targetFields)

      // Create field mappings from suggested mappings
      const mappings: FieldMapping[] = Object.entries(suggestedMappings || {}).map(
        ([hubspotField, salesforceField], index) => ({
          id: `${Date.now()}-${index}`,
          hubspotField,
          salesforceField: String(salesforceField),
        }),
      )

      return mappings
    } catch (err: any) {
      console.error("Error fetching field data:", err)
      const errorMessage = err.userMessage || err.message || "An unexpected error occurred while loading fields"
      setError(errorMessage)
      return []
    } finally {
      setLoading(false)
    }
  }, [getAllFields, location.search])

  return {
    hubspotFields,
    salesforceFields,
    loading,
    error,
    fetchFieldData,
  }
}
